import { useCallback, useEffect, useMemo, useState } from 'react';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import { languageSelectorValidForRegion } from 'pages/about/languageSelectorValidForRegion';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import en from 'state/translations/en.json';
import * as DatadogLogger from 'utils/datadog';
import { isNative } from 'utils/environment';
import { ISOs } from 'utils/form/constants';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { inferLanguage } from './inferLanguage';
import { loadLocale } from './load-locale';
import { LOCALE_DATA } from './locale-data';
import {
  PROD_DEFAULT_LANGUAGE as DEFAULT_LANGUAGE,
  PROD_DEFAULT_LANGUAGE,
  PROD_SUPPORTED_LANGUAGES as SUPPORTED_LANGUAGES
} from './supported-languages';
import { PROD_SUPPORTED_LOCALES, SORTED_PROD_SUPPORTED_LOCALES } from './supported-locales';
import {
  PROD_DEFAULT_REGION as DEFAULT_REGION,
  PROD_SUPPORTED_REGIONS as SUPPORTED_REGIONS
} from './supported-regions';
import { DOMAINS, LANGUAGES, LocaleData, REGIONS } from './types';

export * from './supported-regions';
export * from './supported-languages';

export { SUPPORTED_REGIONS };
export { SUPPORTED_LANGUAGES };

export const LOCALE_SELECTED_QUERY_PARAM = 'locale-selected';
const LOCALE_SEPARATOR = '-';

export const READABLE_LANGUAGES = {
  [LANGUAGES.en]: {
    [LANGUAGES.en]: 'English',
    [LANGUAGES.fr]: 'Français',
    [LANGUAGES.it]: 'Italiano',
    [LANGUAGES.de]: 'Deutsch'
  },
  [LANGUAGES.fr]: {
    [LANGUAGES.en]: 'English',
    [LANGUAGES.fr]: 'Français',
    [LANGUAGES.it]: 'Italiano',
    [LANGUAGES.de]: 'Deutsch'
  },
  [LANGUAGES.it]: {
    [LANGUAGES.en]: 'English',
    [LANGUAGES.fr]: 'Français',
    [LANGUAGES.it]: 'Italiano',
    [LANGUAGES.de]: 'Deutsch'
  },
  [LANGUAGES.de]: {
    [LANGUAGES.en]: 'English',
    [LANGUAGES.fr]: 'Français',
    [LANGUAGES.it]: 'Italiano',
    [LANGUAGES.de]: 'Deutsch'
  }
};

/* @TODO: let's just use formatMessage here instead of redefining for every language */
export const READABLE_REGIONS = {
  [LANGUAGES.en]: {
    [REGIONS.CA]: 'Canada',
    [REGIONS.GB]: 'Great Britain',
    [REGIONS.US]: 'USA',
    [REGIONS.ZA]: 'South Africa',
    [REGIONS.CH]: 'Switzerland'
  },
  [LANGUAGES.fr]: {
    [REGIONS.ZA]: 'Afrique du Sud',
    [REGIONS.CA]: 'Canada',
    [REGIONS.US]: 'États Unis',
    [REGIONS.GB]: 'Grande Bretagne',
    [REGIONS.CH]: 'Switzerland'
  },
  [LANGUAGES.de]: {
    [REGIONS.CA]: 'Kanada',
    [REGIONS.GB]: 'Großbritannien',
    [REGIONS.US]: 'Vereinigte Staaten von Amerika',
    [REGIONS.ZA]: 'Südafrika',
    [REGIONS.CH]: 'Schweiz'
  },
  [LANGUAGES.it]: {
    [REGIONS.CA]: 'Canada',
    [REGIONS.GB]: 'Gran Bretagna',
    [REGIONS.US]: 'Stati Uniti',
    [REGIONS.ZA]: 'Sud Africa',
    [REGIONS.CH]: 'Svizzera'
  }
};

export const REGION_DOMAIN_VALUES = {
  CA: DOMAINS.ca,
  US: DOMAINS.com,
  GB: DOMAINS.uk,
  ZA: DOMAINS.za
};

const regionSpecificLocalStorageKeys = [
  StorageKeys.STORE,
  StorageKeys.AUTH_REDIRECT,
  StorageKeys.ORDER,
  StorageKeys.SERVICE_MODE
];
const reloadWindow = () => document.location.reload();

function findSupportedRegion(requestedRegion: string): SupportedRegions {
  return SUPPORTED_REGIONS[requestedRegion] || DEFAULT_REGION;
}

function findSupportedLanguage(requestedLanguage: string): SupportedLanguages {
  return SUPPORTED_LANGUAGES[requestedLanguage] || DEFAULT_LANGUAGE;
}

function getNavigatorLanguage(): string {
  return (
    (window.navigator &&
      ((window.navigator.languages && window.navigator.languages[0]) ||
        window.navigator.language ||
        // @ts-ignore
        window.navigator.userLanguage)) ||
    ''
  );
}

function inferRegionFromNavigator(): string {
  const navigatorLanguage = getNavigatorLanguage();

  // If the language isn't set return default region
  return navigatorLanguage.split(/.*-/)[1] || String(DEFAULT_REGION);
}

export function inferLanguageFromNavigator(): string {
  const navigatorLanguage = getNavigatorLanguage();
  // If language not set return default language
  return navigatorLanguage.split(/.*-/)[0] || String(PROD_DEFAULT_LANGUAGE);
}

// TODO: Make more robust by using a mapping library (no pun intended)
function inferRegionFromTLD(): string {
  const supportedDomainsRegex = new RegExp(
    ([] as string[])
      .concat(...Object.values(DOMAINS))
      .join('|')
      .replace(/\./g, '\\.')
  );
  const tldMatches = window.location.host.match(supportedDomainsRegex);
  const matchedTld = tldMatches && tldMatches.length && tldMatches[0];

  switch (matchedTld as DOMAINS) {
    case DOMAINS.ca:
      return REGIONS.CA;
    case DOMAINS.uk:
      return REGIONS.GB;
    case DOMAINS.com:
      return REGIONS.US;
    default:
      return String(DEFAULT_REGION);
  }
}

function inferRegionFromUrlParams(): string | null {
  const params = new URL(window.location.href).searchParams;

  const uriRegion = params.get('region');
  return uriRegion;
}

export function loadRegion(): SupportedRegions {
  const inferredDomainRegion = inferRegionFromTLD();
  const inferredUrlParamRegion = inferRegionFromUrlParams();
  const inferredNavigatorRegion = inferRegionFromNavigator();
  // TODO: get the Region form native code.
  // Ideally we would retrieve the user's defined region from native code.
  // As a temporary solution we are checking the users language e.g. en-US or en-CA
  const inferredDomainOrNavigatorRegion = isNative ? inferredNavigatorRegion : inferredDomainRegion;

  const region =
    inferredUrlParamRegion ||
    LocalStorage.getItem<SupportedRegions>(StorageKeys.REGION) ||
    inferredDomainOrNavigatorRegion;

  return findSupportedRegion(region.toString());
}

function inferLanguageFromUrlParams(): string | null {
  const params = new URL(window.location.href).searchParams;

  const uriLang = params.get('lang');

  return uriLang;
}

export function loadLanguage(): SupportedLanguages {
  const navigatorLanguage = getNavigatorLanguage();
  const inferredUrlParamLanguage = inferLanguageFromUrlParams();

  const lang =
    inferredUrlParamLanguage ||
    LocalStorage.getItem<SupportedLanguages>(StorageKeys.LANGUAGE) ||
    navigatorLanguage.split(/-.*/)[0];

  return findSupportedLanguage(lang);
}

export function buildLocaleString(language: string, region: string): string {
  return `${language.toLowerCase()}${LOCALE_SEPARATOR}${region.toUpperCase()}`;
}

function hydrateInitialLocale(useDefault = false): string {
  // if native, uses loadLocale.app.tsx to hydrate based on env variable
  const { language, region } = loadLocale();

  const newLocale = inferSupportedLocale(
    language as SupportedLanguages,
    region as SupportedRegions,
    useDefault
  );

  const [newLocaleLanguage, newLocaleRegion] = newLocale.split(LOCALE_SEPARATOR);

  if (!LocalStorage.getItem(StorageKeys.LANGUAGE) || inferLanguageFromUrlParams()) {
    LocalStorage.setItem(StorageKeys.LANGUAGE, newLocaleLanguage);
  }

  if (!LocalStorage.getItem(StorageKeys.REGION) || inferRegionFromUrlParams()) {
    LocalStorage.setItem(StorageKeys.REGION, newLocaleRegion);
  }

  return newLocale;
}

export function inferSupportedLocale(
  newLanguage: SupportedLanguages,
  newRegion: SupportedRegions,
  useDefault = false
): string {
  const newLocale = buildLocaleString(newLanguage, newRegion);
  const newLocaleEnum = newLocale.toLowerCase().replace(LOCALE_SEPARATOR, '_');

  // This should not happen but just in case
  const defaultLocale = SORTED_PROD_SUPPORTED_LOCALES[0];

  if (useDefault) {
    return defaultLocale;
  }

  if (PROD_SUPPORTED_LOCALES[newLocaleEnum]) {
    return newLocale;
  }

  // Find the first item on the list with the langauge that we care about
  return (
    SORTED_PROD_SUPPORTED_LOCALES.find(sortedLocale => sortedLocale.startsWith(`${newLanguage}`)) ||
    defaultLocale
  );
}

function inferHasShownLanguageSelectorFromUrlParams(): boolean {
  return Boolean(new URL(window.location.href).searchParams.get(LOCALE_SELECTED_QUERY_PARAM));
}

export function inferHasShownLanguageSelector() {
  const hasSelectedLocale = inferHasShownLanguageSelectorFromUrlParams();
  const hasSingleSupportedLocale = SORTED_PROD_SUPPORTED_LOCALES.length < 2;

  return (
    hasSelectedLocale ||
    hasSingleSupportedLocale ||
    !!LocalStorage.getItem<boolean>(StorageKeys.HAS_SHOWN_LOCALE_SELECTOR)
  );
}

export async function getMessagesForLanguage(language: string, region: string) {
  let regionLocale;
  let baseLocale;

  try {
    regionLocale = await import(`state/translations/${language}_${region}.json`);
  } catch (e) {
    logger.warn(e);
  }

  try {
    baseLocale = await import(`state/translations/${language}.json`);
  } catch (e) {
    logger.warn(e);
  }

  return { ...(baseLocale?.default || {}), ...(regionLocale?.default || {}) };
}

/*
 * This Region hook has a few specs to it:
 *
 * 1. It is preloaded with the users inferred region from domain or url params
 * 2. It allows the UI to update the region manually
 * 3. When the users intentionally changes the region, we store that in local storage.
 *    so that we can preload with that selected region in the future.
 * 4. The preloaded locale (language + region) must exist in our list of `SORTED_PROD_SUPPORTED_LOCALES` or fallback to a value on that list.
 * 5. If the user updates their browser region, we should update too.
 */
const useIntl = () => {
  const [messages, setMessages] = useState(en);
  const { updateUserLocale } = useLDContext();
  const isLocalizationDisabled = useFlag(LaunchDarklyFlag.DISABLE_LOCALIZATION);
  const enableAutoLocale = useFlag(LaunchDarklyFlag.ENABLE_AUTO_LOCALE);
  const [locale, setLocale] = useState<string>(hydrateInitialLocale(isLocalizationDisabled));
  const [language, region] = useMemo(() => locale.split(LOCALE_SEPARATOR), [locale]) as [
    SupportedLanguages,
    SupportedRegions
  ];

  const [hasShownLocaleSelector, setInternalHasShownLocaleSelector] = useState<boolean>(
    inferHasShownLanguageSelector()
  );

  const setHasShownLocaleSelector = useCallback(() => {
    LocalStorage.setItem(StorageKeys.HAS_SHOWN_LOCALE_SELECTOR, true);

    setInternalHasShownLocaleSelector(true);
  }, [setInternalHasShownLocaleSelector]);

  const clearRegionSpecificStorage = useCallback(() => {
    // Wipe out region specific keys
    regionSpecificLocalStorageKeys.forEach(LocalStorage.removeItem);
  }, []);

  const setCurrentLocale = useCallback(
    (newLanguage: SupportedLanguages, newRegion: SupportedRegions) => {
      const newLocale = inferSupportedLocale(newLanguage, newRegion, isLocalizationDisabled);
      const [newLocaleLanguage, newLocaleRegion] = newLocale.split(LOCALE_SEPARATOR);

      LocalStorage.setItem(StorageKeys.LANGUAGE, newLocaleLanguage);
      LocalStorage.setItem(StorageKeys.REGION, newLocaleRegion);

      if (newRegion !== region) {
        // Wipe out region specific keys
        clearRegionSpecificStorage();
      }

      // todo: make sure the locale is valid so we dont end up with fr-US
      setLocale(newLocale);
      getMessagesForLanguage(newLocaleLanguage, newLocaleRegion).then(setMessages);
      updateUserLocale({ region: newRegion, language: newLanguage });
      // reload to refresh content that is pulled from Sanity - edge case
      // eg if user's language is english, but they click an external link to french static page
      reloadWindow();
    },
    [isLocalizationDisabled, region, clearRegionSpecificStorage, updateUserLocale]
  );

  useEffectOnce(() => {
    window.addEventListener('languagechange', () => {
      setCurrentLocale(loadLanguage(), loadRegion());
    });
  });

  // We track the user's locale (region + language) in logger errors
  // some errors might occur in specific langauges, so its crucial
  // to know all info on how to reproduce.
  useEffect(() => {
    DatadogLogger.addContext('page_locale', locale);
  }, [locale]);

  useEffectOnce(() => {
    // Make sure we never show the user the dialog again if the params we passed
    if (inferHasShownLanguageSelectorFromUrlParams()) {
      setHasShownLocaleSelector();
    }
    getMessagesForLanguage(language, region).then(setMessages);
  });

  // Make sure locale will update for logged-in users who have localization enabled
  // and also any time the isLocalization flag changes - including when it kicks in initially
  useEffectOnUpdates(() => {
    // remove the region from localStorage so that it doesn't override the domain's region
    localStorage.removeItem(StorageKeys.REGION);
    setCurrentLocale(loadLanguage(), loadRegion());
  }, [isLocalizationDisabled]);

  const { dateFormat } = LOCALE_DATA[locale];

  const blacklistedLangModalRoute = [routes.confirmJwt, routes.store].some(route =>
    window.location.pathname.includes(route)
  );

  const shouldAutoShowSelector =
    useFlag(LaunchDarklyFlag.ENABLE_AUTO_SHOW_REGION_SELECTOR) &&
    !enableAutoLocale &&
    !isLocalizationDisabled &&
    !blacklistedLangModalRoute &&
    !hasShownLocaleSelector &&
    languageSelectorValidForRegion;

  const [showLanguageSelectorModal, setInternalShowLanguageSelectorModal] = useState(
    shouldAutoShowSelector
  );

  const localeDetermined =
    !!LocalStorage.getItem(StorageKeys.DETERMINING_LOCALE_COMPLETE) ||
    inferHasShownLanguageSelector();

  const [determiningLocaleComplete, setDeterminingLocaleCompleteInternal] = useState(
    localeDetermined
  );

  const setDeterminingLocaleComplete = useCallback(() => {
    setDeterminingLocaleCompleteInternal(true);
    LocalStorage.setItem(StorageKeys.DETERMINING_LOCALE_COMPLETE, true);
  }, []);

  const setShowLanguageSelectorModal = useCallback(
    (bool: boolean) => {
      setInternalShowLanguageSelectorModal(bool);
    },
    [setInternalShowLanguageSelectorModal]
  );

  return {
    region,
    feCountryCode: ISOs[region] || ISOs[DEFAULT_REGION],
    language,
    locale,
    inferLanguage,
    findSupportedRegion,
    readableRegions: READABLE_REGIONS[language],
    regionDomainValues: REGION_DOMAIN_VALUES,
    clearRegionSpecificStorage,
    findSupportedLanguage,
    readableLanguages: READABLE_LANGUAGES[language],
    setCurrentLocale,
    hasShownLocaleSelector,
    setHasShownLocaleSelector,
    showLanguageSelectorModal,
    setShowLanguageSelectorModal,
    messages,
    dateFormat,
    determiningLocaleComplete,
    setDeterminingLocaleComplete,
    supportedLocales: SORTED_PROD_SUPPORTED_LOCALES.map(
      supportedLocale => LOCALE_DATA[supportedLocale] as LocaleData
    )
  };
};

export default useIntl;
